import {
    cloneElement,
    FC,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { SwitchTransition } from 'react-transition-group';
import { styled } from 'styled-components';
import { useIsMobile } from '../../utils/getIsMobile';
import { getWindow } from '../../utils/getWindow';
import { useBodyScrollBlock } from '../../utils/useBodyScrollBlock';
import { IconClose } from '../icon/IconClose';
import { IconButton } from '../input/IconButton';
import { Box, BoxProps } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { Flex } from '../layout/Flex';
import { TransitionSlideUp } from '../transition/TransitionSlideUp';
import { Backdrop } from './Backdrop';
import { DrawerContainer, DrawerContent, DrawerTitle } from './Drawer';

interface SmartDrawerProps extends BoxProps {
    onClose: VoidFunction;
    step?: string;
    titleContent?: ((closeHandler: VoidFunction) => ReactNode) | ReactNode;
    mainContent?: ((closeHandler: VoidFunction) => ReactNode) | ReactNode;
    titleStyles?: BoxProps;
    mainStyles?: BoxProps;
    timeout?: number;
    defaultCloseButtonEnabled?: boolean;
    closeButtonSlot?: ReactElement;
    hideTrigger?: boolean;
    forceCloseButton?: boolean;
}

export const SwipeContainer = styled(Flex)
    .withConfig({ displayName: 'SwipeContainer' })
    .attrs((props) => ({
        width: '100%',
        alignItems: 'center',
        justifyContent: 'end',
        maxWidth: '580px',
        ...props,
    }))``;

const SwipeTransformContainer = styled(Flex)
    .withConfig({ displayName: 'SwipeTransformContainer' })
    .attrs((props) => ({
        position: 'relative',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'end',
        ...props,
    }))``;

const SwipeTrigger = styled(Box)
    .withConfig({ displayName: 'SwipeTrigger' })
    .attrs((props) => ({
        width: 34,
        height: 5,
        position: 'absolute',
        top: 1,
        left: '50%',
        background: 'var(--divider)',
        zIndex: 1202,
        transform: 'translateX(-50%)',
        borderRadius: 1,
        ...props,
    }))``;

const SLIDE_TO_CLOSE_LIMIT = 55;
const SWIPE_ACTIVATION_THRESHOLD = 6;

const hasScrolledNestedContainer = (target: EventTarget | null, boundary: HTMLElement) => {
    if (!(target instanceof Element) || !boundary.contains(target)) {
        return false;
    }

    let element: Element | null = target;

    while (element && element !== boundary) {
        const overflowY = element.ownerDocument.defaultView?.getComputedStyle(element).overflowY;

        if (
            (overflowY === 'auto' || overflowY === 'scroll') &&
            element.scrollHeight > element.clientHeight &&
            element.scrollTop > 0
        ) {
            return true;
        }

        element = element.parentElement;
    }

    return false;
};

/**
 * SmartDrawer - Drower, который умеет закрываться свайпом на мобилке и автоматически открывается и закрывается с анимацией.
 * В десктопной версии имеет кнопку закрытия, в мобильной имеет визуальный триггер закрытия.
 * Если вы хотите, чтобы между разными шагами вашего дровера была доп анимация - используйте пропс step
 *
 * @param titleContent - контент заголовка. Или JSX, или функция, принимающая функцию закрытия дровера с анимацией в качестве аргумента и возвращающая JSX
 * @param mainContent - основной контент. Или JSX, или функция, принимающая функцию закрытия дровера с анимацией в качестве аргумента и возвращающая JSX
 * @param onClose - функция закрытия дровера
 * @param step - текущий шаг дровера, передавайте только в том случае, если вы хотите, чтобы между разными шагами вашего дровера была доп анимация
 * @param titleStyles - стили заголовка
 * @param mainStyles - стили основного контента
 * @param timeout - время анимации закрытия дровера
 * @param defaultCloseButtonEnabled - флаг, который определяет, должна ли быть кнопка закрытия дровера
 * @param closeButtonSlot - слот для кнопки закрытия дровера
 * @param hideTrigger - флаг, который определяет, должен ли быть визуальный триггер закрытия дровера
 * @param forceCloseButton - показать кнопку закрытия дровера даже на тачах
 * @param other - остальные пропсы дровера (копирует пропсы Box)
 */
export const SmartDrawer: FC<SmartDrawerProps> = ({
    titleContent,
    mainContent,
    onClose,
    closeButtonSlot,
    step,
    titleStyles,
    mainStyles,
    timeout = 300,
    defaultCloseButtonEnabled = true,
    hideTrigger,
    forceCloseButton,
    ...other
}) => {
    const [swipeContainer, setSwipeContainer] = useState<HTMLDivElement | null>(null);
    const [swipeScroller, setSwipeScroller] = useState<HTMLDivElement | null>(null);
    const [localStep, setLocalStep] = useState<'OPEN' | 'CLOSE'>('OPEN');
    const [currentStep, setCurrentStep] = useState<string>(step || localStep);
    const prevStep = useRef(currentStep);
    const [titleContentWithAnimationDelay, setTitleContentWithAnimationDelay] = useState<
        SmartDrawerProps['titleContent'] | null
    >(null);
    const [mainContentWithAnimationDelay, setMainContentWithAnimationDelay] = useState<
        SmartDrawerProps['mainContent'] | null
    >(null);
    const [mainStylesWithAnimationDelay, setMainStylesWithAnimationDelay] = useState<
        SmartDrawerProps['mainStyles'] | null
    >(null);
    const [titleStylesWithAnimationDelay, setTitleStylesWithAnimationDelay] = useState<
        SmartDrawerProps['titleStyles'] | null
    >(null);

    const isFirstRenderRef = useRef(true);
    const closeTimeoutRef = useRef<number | undefined>(undefined);
    const onCloseRef = useRef(onClose);
    const timeoutRef = useRef(timeout);

    const isMobile = useIsMobile();

    useBodyScrollBlock();

    useLayoutEffect(() => {
        onCloseRef.current = onClose;
        timeoutRef.current = timeout;
    }, [onClose, timeout]);

    const handleClose = useCallback(() => {
        const window = getWindow();

        if (!window || closeTimeoutRef.current !== undefined) {
            return;
        }

        setLocalStep('CLOSE');
        closeTimeoutRef.current = window.setTimeout(() => {
            closeTimeoutRef.current = undefined;
            onCloseRef.current();
        }, timeoutRef.current);
    }, []);

    useEffect(
        () => () => {
            if (closeTimeoutRef.current !== undefined) {
                getWindow()?.clearTimeout(closeTimeoutRef.current);
            }
        },
        [],
    );

    useEffect(() => {
        step && setCurrentStep(step);
    }, [step]);

    useEffect(() => {
        setCurrentStep(localStep);
    }, [localStep]);

    useLayoutEffect(() => {
        if (!swipeContainer || !swipeScroller) {
            return;
        }

        let canDetectSwipe = false;
        let isSwiping = false;
        let startX = 0;
        let startY = 0;
        let delta = 0;

        const resetSwipe = () => {
            canDetectSwipe = false;
            isSwiping = false;
            delta = 0;
            swipeScroller.style.removeProperty('overflow');
            swipeContainer.style.removeProperty('transition');
            swipeContainer.style.transform = 'translateY(0)';
        };

        const onTouchStart = (e: TouchEvent) => {
            if (closeTimeoutRef.current !== undefined) {
                return;
            }

            resetSwipe();

            if (
                e.touches.length !== 1 ||
                swipeScroller.scrollTop > 0 ||
                hasScrolledNestedContainer(e.target, swipeScroller)
            ) {
                return;
            }

            canDetectSwipe = true;
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
        };

        const onTouchEnd = (e: TouchEvent) => {
            if (e.touches.length > 0) {
                resetSwipe();

                return;
            }

            if (delta > SLIDE_TO_CLOSE_LIMIT) {
                handleClose();
                canDetectSwipe = false;
                isSwiping = false;

                return;
            }

            resetSwipe();
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!canDetectSwipe) {
                return;
            }

            if (e.touches.length !== 1) {
                resetSwipe();

                return;
            }

            const currentTouch = e.touches[0];
            const deltaX = currentTouch.clientX - startX;
            const deltaY = currentTouch.clientY - startY;

            if (!isSwiping) {
                const absoluteDeltaX = Math.abs(deltaX);
                const absoluteDeltaY = Math.abs(deltaY);

                if (
                    absoluteDeltaX < SWIPE_ACTIVATION_THRESHOLD &&
                    absoluteDeltaY < SWIPE_ACTIVATION_THRESHOLD
                ) {
                    return;
                }

                if (deltaY <= 0 || absoluteDeltaX >= absoluteDeltaY) {
                    canDetectSwipe = false;

                    return;
                }

                isSwiping = true;
                swipeContainer.style.setProperty('transition', 'none');
            }

            delta = Math.max(deltaY, 0);

            e.preventDefault();
            swipeScroller.style.setProperty('overflow', 'hidden');
            swipeContainer.style.transform = `translateY(${delta}px)`;
        };

        swipeContainer.addEventListener('touchstart', onTouchStart);
        swipeContainer.addEventListener('touchmove', onTouchMove, { passive: false });
        swipeContainer.addEventListener('touchend', onTouchEnd);
        swipeContainer.addEventListener('touchcancel', resetSwipe);

        return () => {
            resetSwipe();
            swipeContainer.removeEventListener('touchstart', onTouchStart);
            swipeContainer.removeEventListener('touchmove', onTouchMove);
            swipeContainer.removeEventListener('touchend', onTouchEnd);
            swipeContainer.removeEventListener('touchcancel', resetSwipe);
        };
    }, [handleClose, swipeContainer, swipeScroller]);

    useEffect(() => {
        prevStep.current = currentStep;

        if (isFirstRenderRef.current || currentStep === prevStep.current) {
            isFirstRenderRef.current = false;

            setTitleContentWithAnimationDelay(() => titleContent);
            setMainContentWithAnimationDelay(() => mainContent);
            setTitleStylesWithAnimationDelay(titleStyles);
            setMainStylesWithAnimationDelay(mainStyles);

            return;
        }

        const timerId = getWindow()?.setTimeout(() => {
            setTitleContentWithAnimationDelay(() => titleContent);
            setMainContentWithAnimationDelay(() => mainContent);
            setTitleStylesWithAnimationDelay(titleStyles);
            setMainStylesWithAnimationDelay(mainStyles);
        }, timeout);

        return () => {
            getWindow()?.clearTimeout(timerId);
        };
    }, [titleContent, titleStyles, mainContent, mainStyles, timeout, currentStep]);

    const isCloseButtonEnabled = (defaultCloseButtonEnabled && !isMobile) || forceCloseButton;

    return (
        <Backdrop onClose={handleClose} justifyContent={isMobile ? 'end' : 'center'}>
            <SwitchTransition>
                <TransitionSlideUp key={currentStep} timeout={timeout}>
                    <SwipeContainer id="swipe-container">
                        <SwipeTransformContainer
                            ref={(node) => setSwipeContainer(node)}
                            transition={`transform ${timeout}ms cubic-bezier(0, 0, 0.2, 1)`}
                        >
                            {isMobile && !hideTrigger ? <SwipeTrigger id="swipe-trigger" /> : null}
                            <DrawerContainer isSmartDrawer {...other}>
                                {titleContent ? (
                                    <DrawerTitle
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        p={2}
                                        {...titleStylesWithAnimationDelay}
                                    >
                                        {titleContentWithAnimationDelay &&
                                        typeof titleContentWithAnimationDelay === 'function'
                                            ? titleContentWithAnimationDelay(handleClose)
                                            : titleContentWithAnimationDelay}
                                        {isCloseButtonEnabled ? (
                                            <Box dataTestId={DataTestId.DRAWER_CLOSE_BUTTON}>
                                                <IconButton onClick={handleClose}>
                                                    <IconClose />
                                                </IconButton>
                                            </Box>
                                        ) : null}
                                    </DrawerTitle>
                                ) : null}

                                {isCloseButtonEnabled && !titleContent ? (
                                    <DrawerTitle
                                        direction="row"
                                        justifyContent="end"
                                        alignItems="center"
                                        p={2}
                                    >
                                        <Box dataTestId={DataTestId.DRAWER_CLOSE_BUTTON}>
                                            <IconButton onClick={handleClose}>
                                                <IconClose />
                                            </IconButton>
                                        </Box>
                                    </DrawerTitle>
                                ) : null}

                                {!titleContent && !isCloseButtonEnabled && closeButtonSlot
                                    ? cloneElement(closeButtonSlot, { onClick: handleClose })
                                    : null}

                                {mainContent ? (
                                    <DrawerContent
                                        ref={(node) => setSwipeScroller(node)}
                                        p={2}
                                        pt={0}
                                        {...mainStylesWithAnimationDelay}
                                    >
                                        {mainContentWithAnimationDelay &&
                                        typeof mainContentWithAnimationDelay === 'function'
                                            ? mainContentWithAnimationDelay(handleClose)
                                            : mainContentWithAnimationDelay}
                                    </DrawerContent>
                                ) : null}
                            </DrawerContainer>
                        </SwipeTransformContainer>
                    </SwipeContainer>
                </TransitionSlideUp>
            </SwitchTransition>
        </Backdrop>
    );
};
